import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { 
  Sparkles, 
  Check,
  ArrowRight,
  Rocket,
  Gift,
  Bell
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import BetaBadge from '@/components/ui/BetaBadge'
import MarketingNav from '@/components/marketing/MarketingNav'
import MarketingFooter from '@/components/marketing/MarketingFooter'

const betaPerks = [
  'Full access to every feature',
  'Unlimited projects during beta',
  'AI command suite included',
  'Direct line to the dev team',
  'Founding member perks at launch',
  'Your feedback shapes the roadmap',
]

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-surface-950">
      <MarketingNav />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh opacity-30" />
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-ai-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <BetaBadge size="lg" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            >
              Pricing is
              <br />
              <span className="text-gradient">coming soon</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-surface-300 mb-10"
            >
              3DMakerly is in public beta - which means everything is 
              <span className="text-white font-semibold"> completely free</span> right now. 
              Sign up and get full access while we polish the platform.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/signup">
                <Button variant="ai" size="lg" className="text-base px-8">
                  <Sparkles className="w-5 h-5" />
                  Join the Beta Free
                </Button>
              </Link>
              <Link to="/features">
                <Button variant="secondary" size="lg" className="text-base px-8">
                  Explore Features
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free During Beta Card */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-900/60 via-surface-900 to-ai-900/40" />
            <div className="absolute inset-0 border-2 border-ai-500/30 rounded-3xl" />
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-ai-500/20 rounded-full blur-3xl" />
            
            <div className="relative p-8 md:p-12">
              <div className="flex flex-col md:flex-row md:items-center gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ai-500/10 border border-ai-500/20 mb-4">
                    <Gift className="w-4 h-4 text-ai-400" />
                    <span className="text-sm font-medium text-ai-300">Beta Access</span>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-3">
                    Every feature. Zero cost.
                  </h2>
                  <p className="text-surface-300 leading-relaxed mb-6">
                    While we're in beta, every account gets the full 3DMakerly experience - 
                    AI design tools, printer management, marketplace access, and more. 
                    No credit card, no trial expiry, no feature gates.
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {betaPerks.map((perk) => (
                      <li key={perk} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-success-500 flex-shrink-0" />
                        <span className="text-surface-200 text-sm">{perk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="md:w-72 flex-shrink-0">
                  <div className="rounded-2xl bg-surface-900/80 border border-surface-700/50 p-6 text-center">
                    <Rocket className="w-10 h-10 text-ai-400 mx-auto mb-4" />
                    <div className="flex items-baseline justify-center gap-1 mb-1">
                      <span className="text-5xl font-bold text-white">£0</span>
                      <span className="text-surface-400">/mo</span>
                    </div>
                    <p className="text-sm text-surface-400 mb-6">during public beta</p>
                    <Link to="/signup" className="block">
                      <Button variant="ai" className="w-full">
                        <Sparkles className="w-4 h-4" />
                        Create Free Account
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What happens at launch */}
      <section className="py-16 bg-surface-900/30 border-y border-surface-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-surface-900/50 border border-surface-700/50"
            >
              <Bell className="w-8 h-8 text-primary-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                We'll tell you before anything changes
              </h3>
              <p className="text-surface-400 leading-relaxed">
                When paid plans launch, beta users get plenty of notice, a free tier that 
                stays genuinely useful, and exclusive founding-member pricing. No surprises, 
                no lock-in - your work is always exportable.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 rounded-2xl bg-surface-900/50 border border-surface-700/50"
            >
              <Sparkles className="w-8 h-8 text-ai-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">
                Help shape what plans look like
              </h3>
              <p className="text-surface-400 leading-relaxed">
                Beta feedback directly informs our pricing tiers. Tell us what you'd pay for, 
                what should stay free, and what limits make sense. Early voices carry the 
                most weight.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <BetaBadge className="mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to start making?
            </h2>
            <p className="text-surface-400 mb-8">
              Free forever during beta. No credit card required.
            </p>
            <Link to="/signup">
              <Button variant="ai" size="lg" className="text-base px-8">
                <Sparkles className="w-5 h-5" />
                Get Started Free
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
